// src/components/DemographicsBarChart.tsx
import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

type MiscDataBySource = {
  followers?: MiscDataEntry[];
  visitors?: MiscDataEntry[];
};

interface MiscDataEntry {
  category: string;
  value: number;
}

type MiscSheetName = "Standort" | "Tätigkeitsbereich" | "Karrierestufe" | "Branche";

const SHEET_OPTIONS: { id: MiscSheetName; label: string }[] = [
  { id: "Standort", label: "Standort" },
  { id: "Tätigkeitsbereich", label: "Tätigkeitsbereich" },
  { id: "Karrierestufe", label: "Karrierestufe" },
  { id: "Branche", label: "Branche" },
];

const TOP_N = 6;
const MAX_LABEL_LENGTH = 20;

function truncateLabel(label: string): string {
  return label.length > MAX_LABEL_LENGTH ? `${label.slice(0, MAX_LABEL_LENGTH - 1)}…` : label;
}

interface DemographicsBarChartProps {
  refreshKey: number;
  accountOptions: string[];
}

export default function DemographicsBarChart({ refreshKey, accountOptions }: DemographicsBarChartProps) {
  const [selectedSheet, setSelectedSheet] = useState<MiscSheetName>("Standort");
  const [selectedAccount, setSelectedAccount] = useState<string>(accountOptions[0] ?? "");
  const [data, setData] = useState<MiscDataEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedAccount) return;
    setIsLoading(true);
    setError(null);

    fetch(`/api/misc-data/${selectedSheet}?account=${encodeURIComponent(selectedAccount)}&source=${"visitors"}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Fehler beim Laden (Status ${res.status})`);
        return res.json();
      })
      .then((result: Record<string, MiscDataBySource>) => {
            const entries = result[selectedAccount]?.visitors ?? [];
            const sorted = [...entries].sort((a, b) => b.value - a.value);
            setTotalCount(sorted.length);
            setData(sorted.slice(0, TOP_N));
        })
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [selectedSheet, selectedAccount, refreshKey]);

  return (
    <div>
      <p> Demographische Daten von Besuchern des Accounts </p>
      <div style={{ marginTop: 16 }}>
        {isLoading && <p>Lade Daten...</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}
        {!isLoading && !error && data.length === 0 && <p>Keine Daten für diese Auswahl vorhanden.</p>}

        {!isLoading && !error && data.length > 0 && (
          <>
            {totalCount > TOP_N && (
              <p style={{ fontSize: 13, color: "#666" }}>
                Top {TOP_N} von {totalCount} Kategorien
              </p>
            )}
            <ResponsiveContainer width="100%" height={Math.max(300, data.length * 45)}>
              <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis
                  type="category"
                  dataKey="category"
                  width={140}
                  tickFormatter={truncateLabel}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                formatter={(value) => [value, "Aufrufe"]}
                labelFormatter={(label) => label}
                />
                <Bar dataKey="value" fill="#008877" barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </div>

        <div className="filter-button-row">
        {accountOptions.map((account) => (
            <button
                key={account}
                className={`filter-button ${selectedAccount === account ? "selected" : ""}`}
                onClick={() => setSelectedAccount(account)}
            >
                {account}
            </button>
        ))}
    </div>

    <div className="filter-button-row" style={{ marginTop: 6 }}>
        {SHEET_OPTIONS.map((option) => (
            <button
                key={option.id}
                className={`filter-button ${selectedSheet === option.id ? "selected" : ""}`}
                onClick={() => setSelectedSheet(option.id)}
            >
                {option.label}
            </button>
        ))}
    </div>

    </div>
  );
}